import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";

interface Stats {
  engines: number;
  transmissions: number;
  bodyParts: number;
  totalProducts: number;
  enquiries: number;
  newEnquiries: number;
  admins: number;
}

export default function Overview() {
  const [stats, setStats] = useState<Stats>({
    engines: 0,
    transmissions: 0,
    bodyParts: 0,
    totalProducts: 0,
    enquiries: 0,
    newEnquiries: 0,
    admins: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [productsRes, enquiriesRes, adminsRes] = await Promise.all([
        supabase.from("products").select("category").is("deleted_at", null),
        supabase.from("enquiries").select("status").is("deleted_at", null),
        supabase.from("admins").select("id", { count: "exact", head: true }),
      ]);

      if (productsRes.error) throw productsRes.error;
      if (enquiriesRes.error) throw enquiriesRes.error;

      const categories = (productsRes.data || []).map((p) => p.category);
      const statuses = (enquiriesRes.data || []).map((e) => e.status);

      setStats({
        engines: categories.filter((c) => c === "Engines").length,
        transmissions: categories.filter((c) => c === "Transmissions").length,
        bodyParts: categories.filter((c) => c === "Body Parts").length,
        totalProducts: categories.length,
        enquiries: statuses.length,
        newEnquiries: statuses.filter((s) => s === "new").length,
        admins: adminsRes.count ?? 0,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const cards = [
    { label: "Engines", value: stats.engines, icon: "ri-settings-3-line", tone: "bg-brand-dark text-brand-gold" },
    { label: "Transmissions", value: stats.transmissions, icon: "ri-settings-4-line", tone: "bg-brand-dark text-brand-gold" },
    { label: "Body Parts", value: stats.bodyParts, icon: "ri-car-washing-line", tone: "bg-brand-dark text-brand-gold" },
    { label: "Total Products", value: stats.totalProducts, icon: "ri-stack-line", tone: "bg-brand-gold text-brand-dark" },
    { label: "New Enquiries", value: stats.newEnquiries, icon: "ri-mail-line", tone: "bg-brand-gold text-brand-dark" },
    { label: "Total Enquiries", value: stats.enquiries, icon: "ri-inbox-line", tone: "bg-brand-dark text-brand-gold" },
    { label: "Admins", value: stats.admins, icon: "ri-shield-user-line", tone: "bg-brand-gold text-brand-dark" },
  ];

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-brand-gold"></i>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
        <p className="text-sm text-red-700">{error}</p>
        <button
          onClick={load}
          className="mt-4 rounded-md bg-brand-dark px-5 py-2 text-sm font-medium text-white hover:bg-black"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="font-heading text-2xl font-semibold uppercase text-brand-dark">Dashboard</h2>
      <p className="mt-1 text-sm text-neutral-500">A snapshot of your inventory and enquiries.</p>

      <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg border border-neutral-200 bg-white p-5">
            <div className="flex items-center justify-between">
              <span className={`flex h-10 w-10 items-center justify-center rounded-md ${c.tone}`}>
                <i className={`${c.icon} text-xl`}></i>
              </span>
              <span className="font-heading text-3xl font-semibold text-brand-dark">{c.value}</span>
            </div>
            <p className="mt-3 text-sm font-medium text-neutral-600">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-lg border border-neutral-200 bg-white p-6">
        <h3 className="font-heading text-lg font-semibold uppercase text-brand-dark">Quick Tips</h3>
        <ul className="mt-3 space-y-2 text-sm text-neutral-600">
          <li className="flex items-start gap-2">
            <i className="ri-checkbox-circle-line mt-0.5 text-brand-gold"></i>
            <span>Add or edit products under the <strong>Products</strong> tab.</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-checkbox-circle-line mt-0.5 text-brand-gold"></i>
            <span>New customer enquiries appear in the <strong>Enquiries</strong> tab.</span>
          </li>
          <li className="flex items-start gap-2">
            <i className="ri-checkbox-circle-line mt-0.5 text-brand-gold"></i>
            <span>Manage your phone numbers, emails, addresses and the sale banner under <strong>Settings</strong>.</span>
          </li>
        </ul>
      </div>
    </div>
  );
}